import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import { buscarMedicaoParaPlanejarCubagem } from '../services/requests/MedicoesInventarioRequests'
import { useMessages } from './useMessages'

import {
	getOrdemServicoById,
	planejarOrdemDeServicoDeCubagem,
	updateOrdemDeServico,
} from '../services/requests/OrdemDeServicoRequests'
import i18n from '../VueI18n'

const { addMessage } = useMessages()

const daps = ref([])
const caps = ref([])

const desabilitarSalvar = ref(false)

const classesEFrequencias = ref([])

const planejamentoCubagem = ref({
	medicaoId: '',
	amostraMinima: undefined,
	amostraMaxima: undefined,
	totalArvoresCubagem: undefined,
	numeroClasses: undefined,
	amplitudeClasses: undefined,
	geofenceEstrato: undefined,
	geofenceParcela: undefined,
	geofenceArvore: undefined,
	metodologiaCubagem: 'DISTANCIAS_FIXAS',
	distanciaMedicoes: 'UM_METRO',
	diametroReferencia: 'DAP',
	showAsDAP: true,
	distribuicaoUniforme: false,
	arredondarClasses: false,
})

//DAP = CAP / Math.PI
//CAP = DAP * Math.PI
watch(
	() => planejamentoCubagem.value.showAsDAP,
	(newValue) => {
		//Se a visualização for em DAP, converte os valores de amostraMinima e amostraMaxima para CAP
		if (newValue) {
			if (planejamentoCubagem.value.amostraMinima) {
				planejamentoCubagem.value.amostraMaxima = planejamentoCubagem.value.amostraMaxima / Math.PI
			}

			if (planejamentoCubagem.value.amostraMaxima) {
				planejamentoCubagem.value.amostraMinima = planejamentoCubagem.value.amostraMinima / Math.PI
			}
		}
		//Se a visualização for em CAP, converte os valores de amostraMinima e amostraMaxima para DAP
		else {
			if (planejamentoCubagem.value.amostraMinima) {
				planejamentoCubagem.value.amostraMaxima = planejamentoCubagem.value.amostraMaxima * Math.PI
			}

			if (planejamentoCubagem.value.amostraMaxima) {
				planejamentoCubagem.value.amostraMinima = planejamentoCubagem.value.amostraMinima * Math.PI
			}
		}
	},
)

const dadosPlanejamentoCubagem = ref(undefined)

export const usePlanejamentoCubagem = () => {
	const objetoOrdemServico = ref(undefined)

	const router = useRouter()

	const estratoFlorestalId = computed(() => router.currentRoute.query.estratoFlorestalId)
	const ordemServicoId = computed(() => router.currentRoute.query.ordemServicoId)
	const executoresIds = ref([...new Set(router.currentRoute.query.executoresIds)])
	const planejamento = computed(() => router.currentRoute.query.planejamento)

	onMounted(() => {
		getOrdemServicoById(ordemServicoId.value).then((response) => {
			objetoOrdemServico.value = response.data.result
		})

		const { metodologiaCubagem, distanciaMedicoes, diametroReferencia } = JSON.parse(
			planejamento.value,
		)
		planejamentoCubagem.value.metodologiaCubagem = metodologiaCubagem
		planejamentoCubagem.value.distanciaMedicoes = distanciaMedicoes
		planejamentoCubagem.value.diametroReferencia = diametroReferencia
	})

	function changedMedicaoId(medicaoId) {
		if (!dadosPlanejamentoCubagem.value) {
			buscarMedicaoParaPlanejarCubagem(medicaoId).then((response) => {
				daps.value = response.data.result.map((m) => m.dap)
				caps.value = response.data.result.map((m) => m.cap)
				planejamentoCubagem.value.totalArvoresCubagem = daps.value.length
				planejamentoCubagem.value.numeroClasses = Math.floor(1 + 3.3 * Math.log(daps.value.length))
			})
		}
	}

	const limiteInferior = computed({
		get() {
			let amostras = planejamentoCubagem.value.showAsDAP ? [...daps.value] : [...caps.value]
			if (planejamentoCubagem.value.amostraMinima)
				amostras = amostras.filter((amostra) => amostra >= planejamentoCubagem.value.amostraMinima)
			const amostraMinimaNum = Number(planejamentoCubagem.value.amostraMinima)
			if (Number.isInteger(amostraMinimaNum)) {
				amostras.push(planejamentoCubagem.value.amostraMinima)
			}
			return Math.min(...amostras)
		},
		set(value) {
			planejamentoCubagem.value.amostraMinima = value
		},
	})

	const limiteSuperior = computed({
		get() {
			let amostras = planejamentoCubagem.value.showAsDAP ? [...daps.value] : [...caps.value]
			if (planejamentoCubagem.value.amostraMaxima)
				amostras = amostras.filter((amostra) => amostra <= planejamentoCubagem.value.amostraMaxima)
			const amostraMaximaNum = Number(planejamentoCubagem.value.amostraMaxima)
			if (Number.isInteger(amostraMaximaNum)) {
				amostras.push(planejamentoCubagem.value.amostraMaxima)
			}
			return Math.max(...amostras)
		},
		set(value) {
			planejamentoCubagem.value.amostraMaxima = value
		},
	})

	const amplitudeTotal = computed(() => {
		const rounded = Math.floor(limiteSuperior.value) - Math.floor(limiteInferior.value)
		return planejamentoCubagem.value.arredondarClasses
			? rounded
			: limiteSuperior.value - limiteInferior.value
	})

	const amplitudeDaClasseComputada = computed(() => {
		return planejamentoCubagem.value.amplitudeClasses
			? planejamentoCubagem.value.amplitudeClasses
			: parseFloat((amplitudeTotal.value / planejamentoCubagem.value.numeroClasses).toFixed(2))
	})

	const amostrasFiltradas = computed(() => {
		let amostras = planejamentoCubagem.value.showAsDAP ? [...daps.value] : [...caps.value]

		let filtradas = amostras.filter(
			(amostra) => amostra >= limiteInferior.value && amostra <= limiteSuperior.value,
		)

		return filtradas
	})

	function calcularClassesEFrequencias() {
		// Definição do início e fim da primeira classe, fazendo a verificação em seguida se precisa arredondar a classe ou não
		let inicioDaClasse = 0
		let fimDaClasse = 0

		if (!planejamentoCubagem.value.arredondarClasses) {
			inicioDaClasse = parseFloat(limiteInferior.value.toFixed(2))
			fimDaClasse = parseFloat((inicioDaClasse + amplitudeDaClasseComputada.value).toFixed(2))
		} else {
			inicioDaClasse = Math.floor(limiteInferior.value)
			fimDaClasse = Math.floor(inicioDaClasse + amplitudeDaClasseComputada.value)
		}

		// Maior valor entre as amostras
		let maiorValor = Math.max(...amostrasFiltradas.value)

		// Valores gerados de classes e frequências
		classesEFrequencias.value = []

		// Faz a iteração nas demais classes
		for (
			let classeAtual = 0;
			classeAtual < planejamentoCubagem.value.numeroClasses;
			classeAtual++
		) {
			const classe = `${inicioDaClasse.toFixed(2)} - ${fimDaClasse.toFixed(2)}`
			const amostrasDaClasse = amostrasFiltradas.value.filter(
				(amostra) => amostra >= inicioDaClasse && amostra <= fimDaClasse,
			)
			const amostraMin = Math.min(...amostrasDaClasse)
			const amostraMax = Math.max(...amostrasDaClasse)
			const freqAbsoluta = amostrasDaClasse.length
			const freqRelativa = (100 * freqAbsoluta) / amostrasFiltradas.value.length
			let freqAbsolutaCubagem = Math.round(
				planejamentoCubagem.value.totalArvoresCubagem * (freqRelativa / 100),
			)

			// Só adiciona a nova classe caso a frequência absoluta para cubar seja maior do que 0
			classesEFrequencias.value.push({
				amostraMin,
				amostraMax,
				classe,
				freqAbsoluta,
				freqRelativa,
				freqAbsolutaCubagem,
			})

			// Faz a verificação se precisa arredondar as classes ou não
			if (!planejamentoCubagem.value.arredondarClasses) {
				inicioDaClasse = parseFloat((fimDaClasse + 0.01).toFixed(2))
				fimDaClasse = parseFloat((inicioDaClasse + amplitudeDaClasseComputada.value).toFixed(2))
			} else {
				inicioDaClasse = Math.ceil(fimDaClasse)
				fimDaClasse = Math.ceil(inicioDaClasse + amplitudeDaClasseComputada.value)
			}

			// Se o final da classe for maior do que o maior valor da lista, o fim da classe recebe este maior valor
			if (fimDaClasse >= maiorValor)
				fimDaClasse = planejamentoCubagem.value.arredondarClasses
					? Math.floor(maiorValor)
					: maiorValor
		}

		if (planejamentoCubagem.value.distribuicaoUniforme)
			classesEFrequencias.value.forEach(
				(f) =>
					(f.freqAbsolutaCubagem = Math.round(
						planejamentoCubagem.value.totalArvoresCubagem / classesEFrequencias.value.length,
					)),
			)
	}

	const series = computed(() => {
		return classesEFrequencias.value.map((classeEFrequencia) => {
			return {
				y: classeEFrequencia.freqAbsolutaCubagem,
				name: classeEFrequencia.classe,
			}
		})
	})

	const categories = computed(() => {
		return classesEFrequencias.value.map(({ classe }) => classe)
	})

	const classeEFrequenciaEditando = ref({})
	function excluirClasse(intervaloDeClassesParaExcluir) {
		// Exclui da lista de classes e Frequencias
		const indiceParaExcluir = classesEFrequencias.value
			.map((classeEFrequencia) => classeEFrequencia.classe)
			.indexOf(intervaloDeClassesParaExcluir)

		classesEFrequencias.value.splice(indiceParaExcluir, 1)

		// Caso esteja editando pelo grafico, limpa o objeto
		classeEFrequenciaEditando.value = {}
	}

	function restaurar() {
		planejamentoCubagem.value.numeroClasses = Math.floor(1 + 3.3 * Math.log(daps.value.length))
		planejamentoCubagem.value.totalArvoresCubagem = daps.value.length
		planejamentoCubagem.value.amplitudeClasses = ''
		planejamentoCubagem.value.amostraMinima = undefined
		planejamentoCubagem.value.amostraMaxima = undefined
		planejamentoCubagem.value.arredondarClasses = false
		planejamentoCubagem.value.distribuicaoUniforme = false
		planejamentoCubagem.value.diametroReferencia = 'DAP'
		planejamentoCubagem.value.metodologiaCubagem = 'DISTANCIAS_FIXAS'
		planejamentoCubagem.value.distanciaMedicoes = 'UM_METRO'
		dadosPlanejamentoCubagem.value = undefined
		calcularClassesEFrequencias()
	}

	function recalcularClasses() {
		calcularClassesEFrequencias()
	}

	const ableToSave = computed(() => {
		const { medicaoId, totalArvoresCubagem, numeroClasses } = planejamentoCubagem.value
		return (
			medicaoId &&
			totalArvoresCubagem &&
			numeroClasses &&
			limiteInferior.value &&
			limiteSuperior.value
		)
	})

	const updateOS = computed(() => {
		return Object.assign(objetoOrdemServico.value, {
			executoresIds: Array.isArray(executoresIds.value)
				? executoresIds.value
				: [executoresIds.value].filter((id) => id),
			orsv_nr_classes: planejamentoCubagem.value.numeroClasses,
			orsv_nr_arvores_cubadas: planejamentoCubagem.value.totalArvoresCubagem,
			orsv_enum_metodologia_cubagem: planejamentoCubagem.value.metodologiaCubagem,
			orsv_enum_tipo_diametro_referencia: planejamentoCubagem.value.diametroReferencia,
			orsv_enum_cubagem_distancias_fixas_distancia:
				planejamentoCubagem.value.metodologiaCubagem === 'DISTANCIAS_FIXAS'
					? planejamentoCubagem.value.distanciaMedicoes
					: undefined,
			orsv_list_cubagem_distancias_relativas_porcentagens:
				planejamentoCubagem.value.metodologiaCubagem === 'DISTANCIAS_RELATIVAS'
					? [
							0.0, 1.0, 2.0, 3.0, 4.0, 5.0, 10.0, 15.0, 25.0, 35.0, 45.0, 55.0, 65.0, 75.0, 85.0,
							95.0,
						]
					: undefined,
		})
	})

	function salvarPlanejamento() {
		const classesComArvoresParaCubar = classesEFrequencias.value.filter(
			(f) => f.freqAbsolutaCubagem > 0,
		)

		const request = {
			OrdemDeServicoId: ordemServicoId.value,
			EstratoFlorestalId: estratoFlorestalId.value,
			MedicaoId: planejamentoCubagem.value.medicaoId,
			NumeroDeClasses: classesComArvoresParaCubar.length,
			NumeroDeArvoresCubagem: planejamentoCubagem.value.totalArvoresCubagem,
			MetodologiaDeCubagem: planejamentoCubagem.value.metodologiaCubagem,
			DistanciaEntreMedicoes: planejamentoCubagem.value.distanciaMedicoes,
			ClassesEFrequencias: classesComArvoresParaCubar.map((classeEFrequencia) => {
				const intervaloClasse = classeEFrequencia.classe.split('-').map(Number)
				return {
					clfr_nm_classe: !planejamentoCubagem.value.showAsDAP
						? `${(intervaloClasse[0] / Math.PI).toFixed(2)} - ${(intervaloClasse[1] / Math.PI).toFixed(2)}`
						: classeEFrequencia.classe,
					clfr_nr_frequencia_absoluta: classeEFrequencia.freqAbsoluta,
					clfr_nr_frequencia_absoluta_cubagem: classeEFrequencia.freqAbsolutaCubagem,
					clfr_nr_frequencia_relativa: classeEFrequencia.freqRelativa,
					clfr_md_dap_minimo: !planejamentoCubagem.value.showAsDAP
						? classeEFrequencia.amostraMin / Math.PI
						: classeEFrequencia.amostraMin,
					clfr_md_dap_maximo: !planejamentoCubagem.value.showAsDAP
						? classeEFrequencia.amostraMax / Math.PI
						: classeEFrequencia.amostraMax,
				}
			}),
		}

		desabilitarSalvar.value = true

		planejarOrdemDeServicoDeCubagem(request)
			.then(async (response) => {
				const { isError } = response.data

				if (!isError) {
					await updateOrdemDeServico(updateOS.value)

					addMessage({ description: i18n.t('sucesso'), type: 'success' })
					router.back()
				} else {
					addMessage({ description: i18n.t('erro'), type: 'error' })
				}
			})
			.catch(() => {
				addMessage({ description: i18n.t('erroAoSalvarDados'), type: 'error' })
			})
			.finally(() => {
				desabilitarSalvar.value = false
			})
	}

	watch(
		planejamentoCubagem,
		() => {
			if (!dadosPlanejamentoCubagem.value) {
				calcularClassesEFrequencias()
			}
		},
		{ deep: true },
	)

	return {
		planejamentoCubagem,
		classesEFrequencias,
		limiteSuperior,
		limiteInferior,
		daps,
		caps,
		amostrasFiltradas,
		series,
		categories,
		excluirClasse,
		classeEFrequenciaEditando,
		restaurar,
		recalcularClasses,
		salvarPlanejamento,
		ordemServicoId,
		estratoFlorestalId,
		changedMedicaoId,
		dadosPlanejamentoCubagem,
		objetoOrdemServico,
		ableToSave,
		amplitudeDaClasseComputada,
		amplitudeTotal,
	}
}
